import { type ReactNode } from 'react';

import { SWRConfig as RootSWRConfig } from 'swr';

import { isHttpError } from '@/src/http';

interface SWRConfigProps {
  readonly children: ReactNode;
}

/**
 * Establishes global configuration for Vercel's {@link useSWR} hook in the context of this
 * application that is consistent with the application's expectations around caching, revalidation,
 * and error handling.
 *
 * The {@link onError} handler enforces that only {@link HttpError} instances are surfaced to
 * per-hook error handlers; non-{@link Error} values and unexpected error subclasses are rethrown
 * so they surface to the global error boundary rather than being silently swallowed by SWR.
 *
 * @see https://swr.vercel.app/
 */
export const SWRConfig = ({ children }: SWRConfigProps) => (
  <RootSWRConfig
    value={{
      errorRetryCount: 0,
      keepPreviousData: true,
      onError: (e: unknown) => {
        if (!(e instanceof Error)) {
          throw new Error(`Client unexpectedly returned non-Error object ${JSON.stringify(e)}`);
        } else if (!isHttpError(e)) {
          throw e;
        }
      },
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateOnReconnect: false,
    }}
  >
    {children}
  </RootSWRConfig>
);
